import React, { useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Flex,
  HStack,
  Text,
  Button,
  Image,
  Spinner,
  Center,
  VStack,
  useMediaQuery,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';
import ReportHeader from './ReportHeader';
import { auditLogsService } from '../services/apiService';
import CaseNavigation from './CaseNavigation';

const PhotoReport = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { cases, samples, traces, loading, loadSamplesByCase, loadTracesBySample, loadingSamples, loadingTraces } = useApp();
  const samplesLoadedRef = useRef(false);
  const tracesLoadedRef = useRef(new Set());

  // Per-image adjustments (persisted per case)
  const storageKey = `photo_report_image_edits_${caseId}`;
  const [imageEdits, setImageEdits] = React.useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [isPrintMode, setIsPrintMode] = React.useState(false);
  const [printProcessing, setPrintProcessing] = React.useState(false);
  const [printImages, setPrintImages] = React.useState({});
  
  // Check if screen is too small for report display (mobile/tablet)
  // Reports are designed for A4 print, so hide on screens smaller than md (768px)
  const [isMobile] = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(imageEdits));
    } catch {
      // ignore
    }
  }, [storageKey, imageEdits]);

  const getEdit = (key) => {
    const e = imageEdits[key];
    return {
      rotate: e?.rotate ?? 0,
    };
  };

  const setEdit = (key, patch) => {
    setImageEdits(prev => ({
      ...prev,
      [key]: { ...getEdit(key), ...patch },
    }));
  };

  const resetEdit = (key) => {
    setImageEdits(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Build a resized image that matches the print frame size (with white padding), so print is consistent.
  const buildPrintImage = async (src, rotateDeg) => {
    // Target "inner frame" size in pixels (approx A4 width area & 11cm height at ~300dpi)
    // 20cm ~= 2362px at 300dpi, 11cm ~= 1299px at 300dpi
    const CANVAS_W = 2360;
    const CANVAS_H = 1300;

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return src;
    }

    // White background (so padding is visible)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const theta = (rotateDeg * Math.PI) / 180;
    const cos = Math.abs(Math.cos(theta));
    const sin = Math.abs(Math.sin(theta));
    const bboxW = w * cos + h * sin;
    const bboxH = w * sin + h * cos;

    const margin = 0.98; // a bit smaller than frame so border remains visible
    const scale = Math.min(CANVAS_W / bboxW, CANVAS_H / bboxH) * margin;

    ctx.save();
    ctx.translate(CANVAS_W / 2, CANVAS_H / 2);
    ctx.rotate(theta);
    ctx.drawImage(img, -(w * scale) / 2, -(h * scale) / 2, w * scale, h * scale);
    ctx.restore();

    return canvas.toDataURL('image/jpeg', 0.92);
  };

  // Reset print mode after printing (must be declared before any early return)
  useEffect(() => {
    const onAfterPrint = () => {
      setIsPrintMode(false);
      setPrintImages({});
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, []);

  // Reset refs when caseId changes
  useEffect(() => {
    samplesLoadedRef.current = false;
    tracesLoadedRef.current.clear();
  }, [caseId]);

  // Load samples for this case when component mounts
  useEffect(() => {
    if (!caseId) return;

    const existingSamples = samples.filter(s => String(s.caseId) === String(caseId));
    if (existingSamples.length === 0 && !samplesLoadedRef.current) {
      samplesLoadedRef.current = true;
      loadSamplesByCase(caseId);
    } else if (existingSamples.length > 0 && !samplesLoadedRef.current) {
      samplesLoadedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, samples]);

  // Load traces when samples are loaded for this case
  useEffect(() => {
    if (!caseId || !samplesLoadedRef.current) return;

    const caseSamples = samples.filter(s => String(s.caseId) === String(caseId));
    caseSamples.forEach(sample => {
      const sampleIdStr = String(sample.id);
      // Check if we already loaded traces for this sample
      if (!tracesLoadedRef.current.has(sampleIdStr)) {
        const existingTraces = traces.filter(t => String(t.sampleId) === sampleIdStr);
        if (existingTraces.length === 0) {
          loadTracesBySample(sample.id);
          tracesLoadedRef.current.add(sampleIdStr);
        } else {
          // Traces already exist, mark as loaded
          tracesLoadedRef.current.add(sampleIdStr);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [samples, caseId]);

  const reportData = useMemo(() => {
    // Ensure we compare IDs as strings (API قد يرجعها أرقام)
    const caseItem = cases.find(c => String(c.id) === String(caseId));
    if (!caseItem) return null;

    const caseSamples = samples.filter(s => String(s.caseId) === String(caseId));
    const allTraces = traces.filter(t => {
      const sample = samples.find(s => String(s.id) === String(t.sampleId));
      return sample && String(sample.caseId) === String(caseId);
    });

    const images = [];
    let imageCounter = 1;

    caseSamples.forEach(sample => {
      if (sample.sampleImage) {
        images.push({
          imageNumber: imageCounter++,
          type: 'sample',
          image: sample.sampleImage,
          caseNumber: caseItem.caseNumber,
          referenceNumber: sample.sampleNumber,
          description: sample.description
        });
      }

      // Ensure we compare IDs as strings
      const sampleTraces = allTraces.filter(t => String(t.sampleId) === String(sample.id));
      sampleTraces.forEach(trace => {
        if (trace.traceImage) {
          images.push({
            imageNumber: imageCounter++,
            type: 'trace',
            image: trace.traceImage,
            caseNumber: caseItem.caseNumber,
            referenceNumber: trace.traceNumber,
            description: trace.status
          });
        }
      });
    });

    return {
      case: caseItem,
      images
    };
  }, [caseId, cases, samples, traces]);

  // Wait for loading to complete before checking if case exists
  if (loading) {
    return null; // Or a loading spinner
  }

  // Show loader while loading samples or traces
  if (loadingSamples || loadingTraces) {
    return (
      <Box p={10} bg="white" borderRadius="md" boxShadow="md">
        <Center>
          <VStack spacing={4}>
            <Spinner size="xl" color="primary.500" thickness="4px" speed="0.65s" />
            <Text color="gray.600">
              {loadingSamples && loadingTraces 
                ? 'جاري تحميل العينات والآثار...' 
                : loadingSamples 
                ? 'جاري تحميل العينات...' 
                : 'جاري تحميل الآثار...'}
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Box p={6} bg="white" borderRadius="md" boxShadow="md">
        <Text mb={4}>القضية غير موجودة</Text>
        <Button onClick={() => navigate('/cases')} colorScheme="primary">
          العودة إلى القضايا
        </Button>
      </Box>
    );
  }

  // Show message on mobile/tablet instead of report content
  if (isMobile) {
    return (
      <>
        <CaseNavigation />
        <Container maxW="1200px" py={10}>
          <Center>
            <VStack spacing={6} textAlign="center" p={8} bg="white" borderRadius="md" boxShadow="lg">
              <Box fontSize="6xl" color="primary.500">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
                </svg>
              </Box>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                تقرير الصور
              </Text>
              <Text fontSize="md" color="gray.600" maxW="500px" lineHeight="1.8">
                هذا التقرير مصمم للعرض والطباعة على شاشة الكمبيوتر. يرجى فتح التقرير على جهاز كمبيوتر أو جهاز لوحي بحجم شاشة أكبر لعرضه وطباعته بشكل صحيح.
              </Text>
              <Button onClick={() => navigate(-1)} colorScheme="primary" size="lg" mt={4}>
                العودة إلى القضية
              </Button>
            </VStack>
          </Center>
        </Container>
      </>
    );
  }

  const handlePrint = async () => {
    // Log print action
    try {
      if (caseId) {
        await auditLogsService.logReportPrint(caseId, 'photo');
      }
    } catch (error) {
      console.error('Failed to log print action:', error);
      // Continue with print even if logging fails
    }

    // Prepare print-ready rasterized images so they fit the frame consistently
    setPrintProcessing(true);
    setIsPrintMode(true);
    try {
      const entries = await Promise.all(
        reportData.images.map(async (it) => {
          const key = String(it.imageNumber);
          const ed = getEdit(key);
          const out = await buildPrintImage(it.image, ed.rotate);
          return [key, out];
        })
      );
      const map = {};
      entries.forEach(([k, v]) => { map[k] = v; });
      setPrintImages(map);

      // Small delay to ensure React renders print mode before printing
      setTimeout(() => {
        window.print();
      }, 150);
    } finally {
      setPrintProcessing(false);
    }
  };

  return (
    <>
      <CaseNavigation />
      <Container 
      maxW="900px"
      sx={{
        '@media print': {
          paddingTop: '0 !important',
          marginTop: '0 !important',
        },
      }}
    >
      <Flex
        justify="space-between"
        mb={6}
        sx={{
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Button onClick={() => navigate('/cases')} variant="outline">
          العودة إلى القضايا
        </Button>
        <Button onClick={handlePrint} colorScheme="primary" isLoading={printProcessing}>
          طباعة التقرير
        </Button>
      </Flex>

      <Box
        bg="white !important"
        p={8}
        boxShadow="md"
        borderRadius="md"
        sx={{
          // Force light mode colors for the report
          bg: 'white !important',
          color: 'gray.800 !important',
          '& *': {
            color: 'inherit',
          },
          '& .chakra-text': {
            color: 'gray.800 !important',
          },
          '@media print': {
            boxShadow: 'none',
            padding: '0 !important',
            margin: '0 !important',
            paddingTop: '0 !important',
            marginTop: '0 !important',
            display: 'block',
            bg: 'white !important',
            color: 'black !important',
          },
        }}
      >
        {reportData.images.length === 0 ? (
          <Box textAlign="center" py={16}>
            <Text color="gray.500" fontSize="lg">لا توجد صور متاحة</Text>
          </Box>
        ) : (
          <Box
            sx={{
              '@media print': {
                margin: '0 !important',
                padding: '0 !important',
                marginTop: '0 !important',
                paddingTop: '0 !important',
              },
            }}
          >
            <style>
              {`
                @media print {
                  @page {
                    size: A4 portrait;
                    margin: 0 !important;
                    padding: 0 !important;
                  }
                  html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    height: 100%;
                  }
                  * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                  }
                  .photo-report-page {
                    page-break-after: always;
                    page-break-inside: avoid;
                    padding-top: 0 !important;
                    margin-top: 0 !important;
                    position: relative;
                    top: 0 !important;
                    left: 0 !important;
                  }
                  .photo-report-page:first-child {
                    padding-top: 0 !important;
                    margin-top: 0 !important;
                    top: 0 !important;
                  }
                  .photo-report-page:last-child {
                    page-break-after: auto;
                  }
                }
              `}
            </style>
            {(() => {
              // تقسيم الصور إلى صفحات (كل صفحة صورتين)
              const pages = [];
              for (let i = 0; i < reportData.images.length; i += 2) {
                pages.push(reportData.images.slice(i, i + 2));
              }
              
              return pages.map((pageImages, pageIndex) => (
                <Box
                  key={pageIndex}
                  className="photo-report-page"
                  as="div"
                  sx={{
                    mb: 4,
                    '@media screen': {
                      mb: 4,
                      minHeight: '800px',
                    },
                    '@media print': {
                      width: '100% !important',
                      height: '26cm !important',
                      minHeight: '26cm !important',
                      maxHeight: '26cm !important',
                      display: 'flex !important',
                      flexDirection: 'column !important',
                      pageBreakBefore: pageIndex === 0 ? 'auto' : 'always',
                      pageBreakAfter: pageIndex < pages.length - 1 ? 'always' : 'auto',
                      pageBreakInside: 'avoid !important',
                      breakBefore: pageIndex === 0 ? 'auto' : 'page',
                      breakAfter: pageIndex < pages.length - 1 ? 'page' : 'auto',
                      breakInside: 'avoid !important',
                      marginTop: '0 !important',
                      marginBottom: '0 !important',
                      marginLeft: '0 !important',
                      marginRight: '0 !important',
                      paddingTop: '0 !important',
                      paddingBottom: '0 !important',
                      paddingLeft: '0 !important',
                      paddingRight: '0 !important',
                      overflow: 'visible !important',
                      boxSizing: 'border-box !important',
                      position: 'relative',
                      top: '0 !important',
                      left: '0 !important',
                    },
                  }}
                >
                  <Box
                    sx={{
                      '@media print': {
                        marginTop: '0 !important',
                        marginBottom: '0.1cm !important',
                        marginLeft: '0 !important',
                        marginRight: '0 !important',
                        paddingTop: '0 !important',
                        paddingBottom: '0 !important',
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                        pageBreakAfter: 'avoid',
                        pageBreakInside: 'avoid',
                        position: 'relative',
                        top: '0',
                      },
                    }}
                  >
                    <ReportHeader
                      date={new Date().toLocaleDateString('ar-SA', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        calendar: 'islamic-umalqura'
                      })}
                      title={`التقرير المصور للقضية رقم `}
                      caseNumber={reportData.case.caseNumber}
                    />
                  </Box>
                  {pageImages.map((item, imageIndex) => (
                    <Box
                      key={item.imageNumber}
                      as="div"
                      sx={{
                        mb: 4,
                        position: 'relative',
                        '@media screen': {
                          mb: 4,
                          minHeight: '400px',
                        },
                        '@media print': {
                          width: '100% !important',
                          height: '11.5cm !important',
                          minHeight: '11.5cm !important',
                          maxHeight: '11.5cm !important',
                          display: 'flex !important',
                          flexDirection: 'column !important',
                          margin: '0 !important',
                          padding: '0 !important',
                          flexShrink: '0 !important',
                          boxSizing: 'border-box !important',
                          justifyContent: 'flex-start !important',
                        },
                }}
              >
                <Box
                  as="div"
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'gray.300',
                    bg: 'white',
                    p: 2,
                    mb: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    '@media print': {
                      height: '11cm !important',
                      minHeight: '11cm !important',
                      maxHeight: '11cm !important',
                      padding: '0.25cm !important',
                      marginBottom: '0 !important',
                      border: '1px solid #000 !important',
                      outline: '1px solid #000 !important',
                      outlineOffset: '0 !important',
                      overflow: 'hidden !important',
                      boxSizing: 'border-box !important',
                      display: 'flex !important',
                      alignItems: 'center !important',
                      justifyContent: 'center !important',
                    },
                  }}
                >
                  <Image
                    src={isPrintMode ? (printImages[String(item.imageNumber)] || item.image) : item.image}
                    alt={`صورة ${item.imageNumber}`}
                          as="img"
                    draggable={false}
                    style={(() => {
                      const key = String(item.imageNumber);
                      const ed = getEdit(key);
                      // Use a CSS variable so print can apply a slightly smaller scale while keeping rotation
                      return {
                        '--photo-rotate': `${ed.rotate}deg`,
                      };
                    })()}
                    sx={{
                            maxH: '100%',
                            maxW: '100%',
                        height: 'auto',
                        width: 'auto',
                        objectFit: 'contain',
                        transform: 'rotate(var(--photo-rotate))',
                        transformOrigin: 'center center',
                            '@media print': {
                              // In print mode we use a rasterized image that already fits the frame.
                              width: '100% !important',
                              height: '100% !important',
                              maxWidth: '100% !important',
                              maxHeight: '100% !important',
                              objectFit: 'contain !important',
                              display: 'block',
                              margin: '0 auto',
                              transform: 'none !important',
                            },
                    }}
                  />
                </Box>
                {/* Controls toolbar (screen only, below frame) */}
                <Box
                  className="no-print"
                  sx={{
                    width: '100%',
                    bg: 'gray.50',
                    border: '1px solid',
                    borderColor: 'gray.200',
                    borderRadius: 'md',
                    p: 2,
                    mb: 2,
                    '@media print': { display: 'none !important' },
                  }}
                >
                  {(() => {
                    const key = String(item.imageNumber);
                    const ed = getEdit(key);
                    return (
                      <VStack align="stretch" spacing={2}>
                        <HStack justify="space-between">
                          <Text fontSize="sm" fontWeight="bold">ضبط الصورة</Text>
                          <Button size="sm" variant="outline" onClick={() => resetEdit(key)}>
                            إعادة ضبط
                          </Button>
                        </HStack>
                        <HStack spacing={2} align="center">
                          <Button size="sm" variant="outline" onClick={() => setEdit(key, { rotate: (ed.rotate - 90) % 360 })}>
                            تدوير يسار
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEdit(key, { rotate: (ed.rotate + 90) % 360 })}>
                            تدوير يمين
                          </Button>
                          <Text fontSize="sm" color="gray.600" minW="80px">
                            {ed.rotate}°
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          يمكنك تدوير الصورة فقط، وسيتم تطبيق نفس الضبط عند الطباعة.
                        </Text>
                      </VStack>
                    );
                  })()}
                </Box>
                <Box
                        as="div"
                  sx={{
                          textAlign: 'center',
                          py: 1,
                          fontSize: 'sm',
                          fontWeight: 'medium',
                    '@media print': {
                            width: '100%',
                            height: '20px',
                            minHeight: '20px',
                            maxHeight: '20px',
                            display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                            fontSize: '9pt',
                            padding: '2px 0',
                            boxSizing: 'border-box',
                    },
                  }}
                >
                        <Text fontSize="sm" fontWeight="medium">
                    <span dir="rtl">رقم الصورة: </span><span dir="ltr">{item.imageNumber}</span>
                    {item.type === 'sample'
                      ? <><span dir="rtl"> - رقم العينة: </span><span dir="ltr">{item.referenceNumber}</span></>
                      : <><span dir="rtl"> - رقم الأثر: </span><span dir="ltr">{item.referenceNumber}</span></>
                    }
                  </Text>
                </Box>
              </Box>
            ))}
                </Box>
              ));
            })()}
          </Box>
        )}
      </Box>
    </Container>
    </>
  );
};

export default PhotoReport;
